import { listInvoices } from '@/api/invoices';
import { listJobsPaged } from '@/api/jobs';
import { listLeads } from '@/api/leadTracker';
import { listPermits } from '@/api/permits';
import { STATUS as INVOICE_STATUS } from '@/components/invoices/invoiceFormat';
import { STAGE_TONE } from '@/components/leads/leadConstants';
import { statusLabel, statusStyle } from '@/lib/jobStatus';
import { isModuleUnlocked } from './helpers';

export const PER_GROUP = 5;
const enc = encodeURIComponent;
const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
const words = (s) => String(s || '').replace(/_/g, ' ');

// Each kind of record the header can find. Matching is each page's own search; rows never carry money.
export const SOURCES = [
  {
    key: 'jobs', title: 'Jobs', noun: 'jobs',
    allowed: ({ can, flags }) => can('view_jobs') && isModuleUnlocked('module_jobs', flags),
    fetch: async (q) => {
      const d = await listJobsPaged({ page: 1, pageSize: PER_GROUP, search: q });
      const total = d.total || 0;
      return { items: d.items || [], total, all: { to: `/jobs?search=${enc(q)}`, label: `See all ${plural(total, 'job')} on Jobs` } };
    },
    open: (j) => `/jobs/${j.id}`,
    row: (j) => ({ title: j.name || 'Untitled job', sub: [j.client_name, j.address], badge: statusLabel(j.status), tone: statusStyle(j.status) }),
  },
  {
    key: 'invoices', title: 'Invoices', noun: 'invoices',
    allowed: ({ can, user }) => !!user?.job_ledger_enabled && can('view_job_financials'),
    fetch: async (q) => {
      const d = await listInvoices({ q, page: 1 });
      const total = d.total || 0;
      return { items: (d.invoices || []).slice(0, PER_GROUP), total, all: { to: `/estimating/invoices?q=${enc(q)}`, label: `See all ${plural(total, 'invoice')} on Invoices` } };
    },
    open: (v) => `/estimating/invoices?invoice=${enc(v.id)}`,
    row: (v) => {
      const s = INVOICE_STATUS[v.display_status] || INVOICE_STATUS.draft;
      return { title: [v.label || 'Draft invoice', v.bill_to?.name].filter(Boolean).join(' · '), sub: [v.job_name, v.po_number && `PO ${v.po_number}`], badge: s.label, tone: `border ${s.cls}` };
    },
  },
  {
    key: 'leads', title: 'Leads', noun: 'leads',
    allowed: ({ can, flags }) => can('view_leads') && isModuleUnlocked('leads_enabled', flags),
    fetch: async (q) => {
      const { data } = await listLeads({ period: 'all', search: q, page: 1, page_size: PER_GROUP });
      const total = data.count || 0;
      return { items: data.items || [], total, all: { to: `/leads?search=${enc(q)}`, label: `See all ${plural(total, 'lead')} on Lead Tracker` } };
    },
    open: (l) => `/leads?lead=${enc(l.id)}`,
    row: (l) => ({ title: l.name || 'Unnamed lead', sub: [l.phone, l.email, l.city], badge: words(l.stage || 'new'), tone: `border ${STAGE_TONE[l.stage] || STAGE_TONE.new}` }),
  },
  {
    key: 'permits', title: 'Permits', noun: 'permits',
    allowed: ({ can, user }) => !!user?.permits_enabled && can('view_permits'),
    // The Permits page lists active and approved permits on separate tabs, so "See all" opens the tab with matches.
    fetch: async (q) => {
      const [a, b] = await Promise.all(['active', 'approved'].map((state) => listPermits({ state, search: q, page: 1, page_size: PER_GROUP })));
      const act = a.data.total || 0;
      const tab = act ? 'active' : 'approved';
      const n = act || b.data.total || 0;
      return {
        items: [...(a.data.items || []), ...(b.data.items || [])].slice(0, PER_GROUP), total: act + (b.data.total || 0),
        all: { to: `/permits?search=${enc(q)}&tab=${tab}`, label: `See all ${n} ${tab} permit${n === 1 ? '' : 's'} on Permits` },
      };
    },
    open: (p) => `/permits?permit=${enc(p.id)}`,
    row: (p) => ({ title: p.client_name || p.job_name || 'Permit', sub: [p.address, p.city_county || p.city, p.permit_number && `#${p.permit_number}`, p.scope],
      badge: p.stage || 'Permit', tone: 'bg-slate-100 text-slate-600' }),
  },
];

export const allowedSources = (ctx) => SOURCES.filter((s) => s.allowed(ctx));

export const placeholderFor = (sources) => `Search ${sources.map((s) => s.noun).join(', ')}…`;
